'use client';
import { useState } from 'react';
import type { ArchNode, ArchEdge, ArchGroup, NodeType } from '@/data/config';

const VW = 1400, VH = 640;
const PADX = 90, PADY = 65;
const NW = 130, NH = 50;
const HW = NW / 2, HH = NH / 2;

const TYPE_COLORS: Record<NodeType, string> = {
  client:   '#FF6B1A',
  server:   '#4a9eff',
  database: '#a78bfa',
  api:      '#50d28c',
  service:  '#fbbf24',
  external: '#88847b',
};

const TYPE_LABELS: Record<NodeType, string> = {
  client:   'CLIENT',
  server:   'SERVER',
  database: 'DB',
  api:      'API',
  service:  'SERVICE',
  external: 'EXTERNAL',
};

function mapX(pct: number) { return PADX + (pct / 100) * (VW - 2 * PADX); }
function mapY(pct: number) { return PADY + (pct / 100) * (VH - 2 * PADY); }

function exitPoint(
  sx: number, sy: number,
  tx: number, ty: number,
  hw: number, hh: number,
): [number, number] {
  const dx = tx - sx, dy = ty - sy;
  if (dx === 0 && dy === 0) return [sx, sy];
  if (Math.abs(dy) * hw <= Math.abs(dx) * hh) {
    const sign = dx > 0 ? 1 : -1;
    return [sx + sign * hw, sy + dy * (hw / Math.abs(dx))];
  }
  const sign = dy > 0 ? 1 : -1;
  return [sx + dx * (hh / Math.abs(dy)), sy + sign * hh];
}

interface Props {
  nodes: ArchNode[];
  edges: ArchEdge[];
  groups?: ArchGroup[];
  title?: string;
}

export default function ArchitectureDiagram({ nodes, edges, groups, title }: Props) {
  const [hovId, setHovId] = useState<string | null>(null);

  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  const connected = new Set<string>();
  if (hovId) {
    connected.add(hovId);
    edges.forEach(e => {
      if (e.from === hovId) connected.add(e.to);
      if (e.to === hovId) connected.add(e.from);
    });
  }

  const any = hovId !== null;
  const nodeOp = (id: string) => any ? (connected.has(id) ? 1 : 0.1) : 1;
  const edgeOp = (e: ArchEdge) =>
    any ? (connected.has(e.from) && connected.has(e.to) ? 1 : 0.05) : 0.75;

  const ncx = (n: ArchNode) => mapX(n.x);
  const ncy = (n: ArchNode) => mapY(n.y);

  const usedTypes = [...new Set(nodes.map(n => n.type))];
  const hasDash   = edges.some(e => e.style === 'dashed');

  // Group box layout, computed once and shared by the group-rect render pass
  // below and the label-obstacle list, so the two never disagree.
  const groupLayout = (groups ?? []).map(group => {
    const gns = group.nodeIds.map(id => nodeMap.get(id)).filter(Boolean) as ArchNode[];
    if (gns.length === 0) return null;
    const xs = gns.map(n => ncx(n));
    const ys = gns.map(n => ncy(n));
    const pad = 22;
    const bx = Math.min(...xs) - HW - pad;
    const by = Math.min(...ys) - HH - pad;
    const bw = Math.max(...xs) - Math.min(...xs) + NW + pad * 2;
    const bh = Math.max(...ys) - Math.min(...ys) + NH + pad * 2;
    return { bx, by, bw, bh };
  });

  const boxesOverlap = (
    a: { x0: number; x1: number; y0: number; y1: number },
    b: { x0: number; x1: number; y0: number; y1: number },
  ) => a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;

  // Obstacles edge labels must clear: every node box, plus every group's
  // opaque header strip (the rest of a group rect is translucent, so a label
  // over it stays legible — only the solid header bar actually hides text).
  const labelObstacles = [
    ...nodes.map(n => {
      const cx = ncx(n), cy = ncy(n);
      return { x0: cx - HW, x1: cx + HW, y0: cy - HH, y1: cy + HH };
    }),
    ...groupLayout.flatMap(g => g ? [{ x0: g.bx, x1: g.bx + g.bw, y0: g.by, y1: g.by + 18 }] : []),
  ];

  // Edge line + label geometry, computed once. Lines are drawn before nodes
  // (so node rects sit cleanly on top of line ends at the ports); labels are
  // drawn in a separate pass *after* nodes so they're never hidden behind an
  // opaque node rect. Label placement additionally walks outward along the
  // perpendicular until it clears every node box, so a label never lands on
  // top of an unrelated node it happens to pass near — regardless of how
  // tightly a given diagram is packed.
  const edgeGeom = edges.flatMap((edge, i) => {
    const src = nodeMap.get(edge.from);
    const tgt = nodeMap.get(edge.to);
    if (!src || !tgt) return [];

    const sx = ncx(src), sy = ncy(src);
    const tx = ncx(tgt), ty = ncy(tgt);
    const [rp1x, rp1y] = exitPoint(sx, sy, tx, ty, HW + 2, HH + 2);
    const [rp2x, rp2y] = exitPoint(tx, ty, sx, sy, HW + 2, HH + 2);

    // perpendicular offset for parallel edges
    const rdx = rp2x - rp1x, rdy = rp2y - rp1y;
    const rlen = Math.hypot(rdx, rdy) || 1;
    const off = edge.offset ?? 0;
    const nxv = -rdy / rlen, nyv = rdx / rlen;
    const p1x = rp1x + nxv * off, p1y = rp1y + nyv * off;
    const p2x = rp2x + nxv * off, p2y = rp2y + nyv * off;

    const color  = TYPE_COLORS[src.type];
    const op     = edgeOp(edge);
    const isDash = edge.style === 'dashed';
    const isBidi = edge.direction === 'bidirectional';
    const mx     = (p1x + p2x) / 2;
    const my     = (p1y + p2y) / 2;
    const dx     = p2x - p1x;
    const dy     = p2y - p1y;
    const len    = Math.hypot(dx, dy) || 1;

    // unit perpendicular, oriented to prefer sitting above the line
    let ux = -dy / len, uy = dx / len;
    if (uy >= 0) { ux = -ux; uy = -uy; }

    const lbl = edge.label?.toUpperCase() ?? '';
    const lblW = lbl.length * 5.6 + 10;

    let lx = mx, ly = my;
    if (lbl) {
      let dist = 11;
      for (let tries = 0; tries < 8; tries++) {
        lx = mx + ux * dist;
        ly = my + uy * dist;
        const box = { x0: lx - lblW / 2, x1: lx + lblW / 2, y0: ly - 7, y1: ly + 7 };
        if (!labelObstacles.some(ob => boxesOverlap(ob, box))) break;
        dist += 9;
      }
    }

    return [{
      key: `edge-${i}`, p1x, p1y, p2x, p2y, color, op, isDash, isBidi,
      lbl, lblW, lx, ly,
    }];
  });

  return (
    <div className="arch-diagram-wrap" style={{ position: 'relative' }}>
      {title && (
        <p className="label" style={{ marginBottom: 16 }}>{title}</p>
      )}

      <p
        style={{
          position: 'absolute',
          bottom: 50,
          left: '50%',
          transform: 'translateX(-50%)',
          fontFamily: 'monospace',
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--ink-4)',
          opacity: hovId ? 0 : 1,
          transition: 'opacity 0.3s',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}
      >
        hover to explore
      </p>

      <svg
        viewBox={`0 0 ${VW} ${VH}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        aria-label={title ? `${title} — architecture diagram` : 'System architecture diagram'}
      >
        <defs>
          <style>{`
            @keyframes ad-flow { to { stroke-dashoffset: -32; } }
            .ad-flow { animation: ad-flow 1.2s linear infinite; }
            @keyframes ad-pulse { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }
            .ad-pulse { animation: ad-pulse 2.4s ease-in-out infinite; }
          `}</style>
          <marker
            id="ad-end"
            markerWidth="10" markerHeight="8"
            refX="9" refY="4"
            orient="auto"
            markerUnits="userSpaceOnUse"
          >
            <path d="M 0 0 L 9 4 L 0 8 z" fill="#6b6760" />
          </marker>
          <marker
            id="ad-start"
            markerWidth="10" markerHeight="8"
            refX="9" refY="4"
            orient="auto-start-reverse"
            markerUnits="userSpaceOnUse"
          >
            <path d="M 0 0 L 9 4 L 0 8 z" fill="#6b6760" />
          </marker>
          <filter id="ad-glow">
            <feGaussianBlur stdDeviation="4" />
          </filter>
          <pattern
            id="ad-dots"
            x="0" y="0" width="32" height="32"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="16" cy="16" r="0.7" fill="#26262b" />
          </pattern>
          {/* Per-type subtle radial fill for nodes */}
          {(Object.keys(TYPE_COLORS) as NodeType[]).map(t => (
            <radialGradient key={`grad-${t}`} id={`ad-grad-${t}`} cx="50%" cy="0%" r="100%">
              <stop offset="0%"  stopColor={TYPE_COLORS[t]} stopOpacity="0.10" />
              <stop offset="60%" stopColor={TYPE_COLORS[t]} stopOpacity="0.02" />
              <stop offset="100%" stopColor={TYPE_COLORS[t]} stopOpacity="0" />
            </radialGradient>
          ))}
        </defs>

        {/* dot grid */}
        <rect width={VW} height={VH} fill="url(#ad-dots)" opacity="0.65" />

        {/* Tier groups — drawn behind everything */}
        {groups?.map((group, gi) => {
          const layout = groupLayout[gi];
          if (!layout) return null;
          const { bx, by, bw, bh } = layout;

          const gns = group.nodeIds
            .map(id => nodeMap.get(id))
            .filter(Boolean) as ArchNode[];

          // dim group when hover excludes all its nodes
          const groupActive = !any || gns.some(n => connected.has(n.id));
          const op = groupActive ? 1 : 0.25;

          return (
            <g
              key={`group-${gi}`}
              style={{ opacity: op, transition: 'opacity 0.22s' }}
            >
              <rect
                x={bx} y={by} width={bw} height={bh}
                rx="6"
                fill="var(--surface)"
                fillOpacity="0.4"
                stroke="var(--line)"
                strokeWidth="0.8"
                strokeDasharray="5 5"
              />
              <rect
                x={bx} y={by}
                width={bw} height={18}
                rx="6"
                fill="var(--paper-2)"
                stroke="none"
              />
              <text
                x={bx + 12} y={by + 12}
                fontFamily="monospace"
                fontSize="8.5"
                letterSpacing="0.18em"
                fill="var(--ink-3)"
              >
                {group.label.toUpperCase()}
              </text>
              <text
                x={bx + bw - 12} y={by + 12}
                fontFamily="monospace"
                fontSize="8.5"
                letterSpacing="0.16em"
                fill="var(--ink-4)"
                textAnchor="end"
              >
                {String(gi + 1).padStart(2, '0')}
              </text>
            </g>
          );
        })}

        {/* Edge lines (labels are drawn after nodes, see below) */}
        {edgeGeom.map(g => (
          <g key={g.key} style={{ opacity: g.op, transition: 'opacity 0.22s' }}>
            <line
              x1={g.p1x} y1={g.p1y} x2={g.p2x} y2={g.p2y}
              stroke={g.color}
              strokeWidth={1.5}
              strokeOpacity="0.6"
              strokeDasharray={g.isDash ? '8 8' : undefined}
              className={g.isDash ? 'ad-flow' : undefined}
              markerEnd="url(#ad-end)"
              markerStart={g.isBidi ? 'url(#ad-start)' : undefined}
            />
          </g>
        ))}

        {/* Nodes */}
        {nodes.map(node => {
          const x  = ncx(node), y = ncy(node);
          const c  = TYPE_COLORS[node.type];
          const op = nodeOp(node.id);
          const isHov = hovId === node.id;

          return (
            <g
              key={node.id}
              style={{ opacity: op, transition: 'opacity 0.22s', cursor: 'pointer' }}
              onMouseEnter={() => setHovId(node.id)}
              onMouseLeave={() => setHovId(null)}
            >
              {isHov && (
                <rect
                  x={x - HW - 14} y={y - HH - 14}
                  width={NW + 28} height={NH + 28}
                  rx="8"
                  fill={c}
                  opacity="0.22"
                  filter="url(#ad-glow)"
                />
              )}

              {/* node body */}
              <rect
                x={x - HW} y={y - HH}
                width={NW} height={NH}
                rx="3"
                fill="var(--paper)"
                stroke={c}
                strokeWidth={isHov ? 1.5 : 1}
              />
              {/* gradient overlay for depth */}
              <rect
                x={x - HW} y={y - HH}
                width={NW} height={NH}
                rx="3"
                fill={`url(#ad-grad-${node.type})`}
                pointerEvents="none"
              />

              {/* left rail accent */}
              <rect
                x={x - HW} y={y - HH}
                width={3} height={NH}
                fill={c}
                opacity={isHov ? 1 : 0.85}
              />

              {/* type tag (top-right) */}
              <rect
                x={x + HW - 4 - (TYPE_LABELS[node.type].length * 4.4 + 8)}
                y={y - HH + 4}
                width={TYPE_LABELS[node.type].length * 4.4 + 8}
                height={10}
                rx="1"
                fill={c}
                opacity="0.16"
              />
              <text
                x={x + HW - 8} y={y - HH + 9}
                textAnchor="end"
                dominantBaseline="middle"
                fontFamily="monospace"
                fontSize="6.8"
                letterSpacing="0.16em"
                fill={c}
                opacity={isHov ? 1 : 0.9}
              >
                {TYPE_LABELS[node.type]}
              </text>

              {/* main label */}
              <text
                x={x - HW + 11}
                y={y - 3}
                dominantBaseline="middle"
                fontFamily="monospace"
                fontSize="11.5"
                fontWeight={isHov ? '600' : '500'}
                fill="var(--ink)"
                letterSpacing="0.01em"
              >
                {node.label}
              </text>

              {/* sublabel */}
              {node.sublabel && (
                <text
                  x={x - HW + 11}
                  y={y + 11}
                  dominantBaseline="middle"
                  fontFamily="monospace"
                  fontSize="8.2"
                  fill="var(--ink-3)"
                  letterSpacing="0.04em"
                >
                  {node.sublabel}
                </text>
              )}

              {/* status dot */}
              <circle
                cx={x - HW + 6} cy={y + HH - 5}
                r="1.8"
                fill={c}
                className="ad-pulse"
              />
            </g>
          );
        })}

        {/* Edge labels — drawn last so they always sit on top of node rects */}
        {edgeGeom.map(g => g.lbl && (
          <g
            key={`${g.key}-label`}
            transform={`translate(${g.lx}, ${g.ly})`}
            style={{ opacity: g.op, transition: 'opacity 0.22s' }}
          >
            <rect
              x={-g.lblW / 2} y={-7}
              width={g.lblW} height={14}
              rx="2"
              fill="var(--paper)"
              stroke={g.color}
              strokeWidth="0.4"
              strokeOpacity="0.45"
            />
            <text
              x={0} y={1}
              textAnchor="middle"
              dominantBaseline="middle"
              fontFamily="monospace"
              fontSize="8"
              letterSpacing="0.1em"
              fill={g.color}
              opacity="0.95"
            >
              {g.lbl}
            </text>
          </g>
        ))}
      </svg>

      {/* legend */}
      {(usedTypes.length > 0 || hasDash) && (
        <div
          style={{
            display: 'flex',
            gap: 18,
            flexWrap: 'wrap',
            marginTop: 14,
            paddingTop: 14,
            borderTop: '1px solid var(--line)',
            alignItems: 'center',
          }}
        >
          {usedTypes.map(type => (
            <div
              key={type}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                fontFamily: 'var(--mono)',
                fontSize: 10,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--ink-3)',
              }}
            >
              <span
                style={{
                  width: 10, height: 10,
                  background: TYPE_COLORS[type],
                  borderRadius: 2,
                  flexShrink: 0,
                  opacity: 0.85,
                }}
              />
              {type}
            </div>
          ))}

          {hasDash && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                fontFamily: 'var(--mono)',
                fontSize: 10,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--ink-3)',
              }}
            >
              <svg width="28" height="10" aria-hidden="true" style={{ flexShrink: 0 }}>
                <line x1="0" y1="5" x2="28" y2="5" stroke="#6b6760" strokeWidth="1.5" strokeDasharray="4 6" />
              </svg>
              Async / external
            </div>
          )}
        </div>
      )}
    </div>
  );
}
